
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { EPermissionActions } from '@/model/EPermissionActions';
import { IAuthUserData } from '@/store/modules/authModule';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { EUserPermissions } from '@/model/EUserPermissions';
import { ICommunity, IModule } from '@/model/Community/communityInterfaces';

const authModule = namespace('authModule');
const communityModule = namespace('communityModule');

interface NavTab {
  route: string;
  icon: string;
  labelKey: string;
  label?: string;
  action?: string;
  requiresCommunityId?: boolean;
  requiresAdminRole?: boolean;
  requiresModuleSlug?: string;
  moduleSlug?: string;
}

@Component({ name: 'CommunityNav' })
export default class CommunityNav extends Vue {
  @Prop()
  protected communityId!: number;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @communityModule.Getter('community')
  protected community!: ICommunity | null;

  @communityModule.Getter('availableModules')
  protected availableModules!: IModule[];

  @communityModule.Action('fetchModules')
  private fetchModules!: () => void;

  private allTabs: NavTab[] = [
    { route: 'community',              icon: 'mdi-domain',               labelKey: 'communities.nav.details',         requiresCommunityId: true },
    { route: 'communityEdit',          icon: 'mdi-pencil',               labelKey: 'communities.nav.edit',            requiresCommunityId: true, action: EPermissionActions.ACTION_COMMUNITIES_UPDATE },
    { route: 'communityLockers',       icon: 'mdi-package-variant-closed', labelKey: 'communities.nav.lockers',       requiresCommunityId: true, requiresModuleSlug: 'locker' },
    { route: 'communityStandalonesList', icon: 'mdi-cellphone-link',     labelKey: 'communities.nav.standalones',     requiresCommunityId: true, requiresModuleSlug: 'standalone' },
    { route: 'communityB2bInvites',    icon: 'mdi-link',                 labelKey: 'communities.nav.invitationLinks', requiresCommunityId: true, requiresAdminRole: true },
    { route: 'communityContacts',      icon: 'mdi-account-group',        labelKey: 'communities.nav.contacts',        requiresCommunityId: true },
    { route: 'communityOrders',        icon: 'mdi-cart',                 labelKey: 'communities.nav.orders',           requiresCommunityId: true },
    { route: 'communityServiceProviders', icon: 'mdi-store',             labelKey: 'communities.nav.serviceProviders', requiresCommunityId: true },
    { route: 'communityUsers',         icon: 'mdi-account-multiple',     labelKey: 'communities.nav.users',           requiresCommunityId: true },
  ];

  created() {
    if (this.$canAction(EPermissionActions.ACTION_COMMUNITIES_ADMIN)) {
      this.fetchModules();
    }
  }

  get moduleTabs(): NavTab[] {
    if (!this.communityId || !this.community || !this.community.modules || !this.availableModules) return [];
    if (!this.$canAction(EPermissionActions.ACTION_COMMUNITIES_ADMIN)) return [];
    return this.community.modules
      .map((enabled: IModule) => this.availableModules.find((each: IModule) => each.id === enabled.id))
      .filter((mod: IModule | undefined): mod is IModule => !!mod && !!mod.submodules && mod.submodules.length > 0)
      .map((mod: IModule) => ({
        route: 'communityModule',
        icon: mod.icon || 'mdi-cube-outline',
        labelKey: '',
        label: mod.name,
        requiresCommunityId: true,
        moduleSlug: mod.slug,
      }));
  }

  private communityHasModule(slug: string): boolean {
    if (!this.community || !this.community.modules) return true;
    return this.community.modules.some((m: any) => m.slug === slug);
  }

  get visibleTabs(): NavTab[] {
    return this.allTabs
      .filter(tab => {
        if (tab.requiresCommunityId && !this.communityId) return false;
        if (tab.requiresAdminRole && !this.canAccessAdminTabs()) return false;
        if (tab.action && !this.$canAction(tab.action)) {
          if (!PermissionHelper.hasRoleAdministrator(this.authUser)) return false;
        }
        if (tab.requiresModuleSlug && !this.communityHasModule(tab.requiresModuleSlug)) return false;
        return true;
      })
      .map((tab): NavTab => ({
        ...tab,
        label: this.$t(tab.labelKey) as string,
      }))
      .concat(this.moduleTabs);
  }

  private canAccessAdminTabs(): boolean {
    return PermissionHelper.hasRoleAdministrator(this.authUser)
      && (PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_LOCKER_OWNER)
        || PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_LOCKER_OPERATOR));
  }

  get activeTabIndex(): number {
    return this.visibleTabs.findIndex(tab => this.isActive(tab));
  }

  tabKey(tab: NavTab): string {
    return tab.moduleSlug ? tab.route + ':' + tab.moduleSlug : tab.route;
  }

  tabRoute(tab: NavTab): any {
    if (tab.requiresCommunityId && this.communityId) {
      const params: { [key: string]: string } = { communityId: String(this.communityId) };
      if (tab.moduleSlug) params.moduleSlug = tab.moduleSlug;
      return { name: tab.route, params };
    }
    return { name: tab.route };
  }

  isActive(tab: NavTab): boolean {
    return this.$route.name === tab.route && (!tab.moduleSlug || this.$route.params.moduleSlug === tab.moduleSlug);
  }
}
